import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { getCategoryTranslator } from '../utils/categoryTranslation';
import { nativeFetch } from '../utils/nativeApi';

const CURRENCIES = ['ILS', 'USD', 'EUR', 'GEL'];

function ExpenseEditModal({ expense, isOpen, onClose, onSave, onDelete }) {
  const { t } = useTranslation(['expenses', 'common', 'categories']);
  const translateCategory = getCategoryTranslator(t);
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    currency: 'ILS',
    category_id: '',
    payment_method_id: '',
    note: ''
  });
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch categories and payment methods
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Populate form when expense changes
  useEffect(() => {
    if (expense) {
      setFormData({
        title: expense.title || '',
        amount: expense.amount || '',
        currency: expense.currency || 'ILS',
        category_id: expense.category?.id || '',
        payment_method_id: expense.payment_method?.id || '',
        note: expense.note || ''
      });
    }
  }, [expense]);

  const fetchCategories = async () => {
    try {
      const response = await nativeFetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await nativeFetch('/api/payment-methods');
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title || !formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        title: formData.title.trim(),
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        category_id: formData.category_id || null,
        payment_method_id: formData.payment_method_id || null,
        note: formData.note.trim() || null
      };

      const response = await nativeFetch(`/api/expenses/${expense.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Expense updated successfully!');
        onSave(data.expense);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update expense');
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Failed to update expense');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);

    try {
      const response = await nativeFetch(`/api/expenses/${expense.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success(t('toast.deleted'));
        if (onDelete) onDelete(expense.id);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete expense');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-black">{t('editExpense')}</h2>
          <button
            onClick={onClose}
            className="text-3xl font-bold hover:scale-110 transition-transform"
          >
            ✕
          </button>
        </div>

        {/* Logged on timestamp */}
        {expense?.created_at && (
          <p className="text-sm text-gray-500 mb-4">
            {t('form.loggedOn')}: {new Date(expense.created_at * 1000).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-bold mb-2">{t('form.title')} *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t('form.titlePlaceholder')}
              required
            />
          </div>

          {/* Amount & Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-2">{t('form.amount')} *</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-4 py-3 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">{t('form.currency')} *</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-3 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                required
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-bold mb-2">{t('form.category')}</label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              className="w-full px-4 py-3 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="">{t('form.selectCategory')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {translateCategory(cat.name)}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-bold mb-2">{t('form.paymentMethod')}</label>
            <select
              value={formData.payment_method_id}
              onChange={(e) => setFormData({ ...formData, payment_method_id: e.target.value })}
              className="w-full px-4 py-3 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="">{t('form.selectPaymentMethod')}</option>
              {paymentMethods.map(pm => (
                <option key={pm.id} value={pm.id}>{pm.name}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-bold mb-2">{t('form.note')}</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              className="w-full px-4 py-3 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows="3"
              placeholder={t('form.notePlaceholder')}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={handleDeleteClick}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg font-bold bg-red-500 text-white shadow-hard-sm hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
              disabled={loading || deleting}
            >
              {deleting ? '...' : `🗑️ ${t('common:buttons.delete')}`}
            </button>
            <button
              type="submit"
              disabled={loading || deleting}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg font-bold bg-primary text-black shadow-hard-sm hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
            >
              {loading ? '...' : `💾 ${t('common:buttons.save')}`}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg font-bold bg-white text-black shadow-hard-sm hover:bg-gray-100 transition-colors disabled:opacity-50 text-sm"
              disabled={loading || deleting}
            >
              ↩️ {t('common:buttons.cancel')}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

export default ExpenseEditModal;
